// Pure-presentational dropzone + progress card shared between the
// raw-profiles publisher and the API-tarball publisher. Owns no upload
// state itself; the parent passes in the result of `useTusUpload` plus
// any per-channel display tweaks (accept set, idle hint).

import { CheckCircle2, Loader2, UploadCloud, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { type ReactNode, useCallback } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { Button } from "../components/button";
import { Progress } from "../components/progress";
import { formatBytes } from "../lib/format";
import { cn } from "../lib/utils";
import {
	fmtEta,
	fmtRate,
	type UploadState,
	type UploadStats,
} from "./use-tus-upload";

const EASE = [0.22, 1, 0.36, 1] as const;

interface UploadDropzoneProps {
	state: UploadState;
	file: File | null;
	stats: UploadStats;
	error: string | null;
	doneSummary?: ReactNode;
	idleHint: ReactNode;
	accept: Accept;
	onDrop: (file: File) => void;
	onCancel: () => void;
	onRetry: () => void;
	onReset: () => void;
}

export function UploadDropzone({
	state,
	file,
	stats,
	error,
	doneSummary,
	idleHint,
	accept,
	onDrop,
	onCancel,
	onRetry,
	onReset,
}: UploadDropzoneProps) {
	const reduce = useReducedMotion();
	const handleDrop = useCallback(
		(files: File[]) => {
			const f = files[0];
			if (f) onDrop(f);
		},
		[onDrop],
	);
	const dropzone = useDropzone({
		onDrop: handleDrop,
		maxFiles: 1,
		multiple: false,
		disabled: state === "uploading",
		accept,
	});

	const pct =
		stats.total > 0 ? Math.min(100, (stats.loaded / stats.total) * 100) : 0;

	const fade = reduce
		? {}
		: {
				initial: { opacity: 0, y: 6 },
				animate: { opacity: 1, y: 0 },
				exit: { opacity: 0, y: -4 },
				transition: { duration: 0.2, ease: EASE },
			};

	return (
		<AnimatePresence mode="wait" initial={false}>
			{state === "idle" ? (
				<motion.div
					key="idle"
					{...fade}
					whileHover={reduce ? undefined : { scale: 1.005 }}
					{...dropzone.getRootProps({
						className: cn(
							"flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-8 text-sm transition-colors",
							dropzone.isDragActive
								? "border-primary bg-primary/5 ring-4 ring-primary/10"
								: "border-border",
						),
					})}
				>
					<input {...dropzone.getInputProps()} />
					<UploadCloud className="mb-2 size-8 text-muted-foreground" />
					<div className="text-center">{idleHint}</div>
				</motion.div>
			) : null}

			{state === "uploading" && file ? (
				<motion.div key="uploading" {...fade} className="rounded-md border p-4">
					<div className="flex items-center justify-between gap-3 text-sm">
						<span className="truncate font-mono">{file.name}</span>
						<span className="tabular-nums text-muted-foreground">
							{formatBytes(stats.loaded)} / {formatBytes(stats.total)}
						</span>
					</div>
					<Progress value={pct} className="mt-3 h-1.5" />
					<div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
						<span className="tabular-nums">
							{pct.toFixed(1)}% · {fmtRate(stats.rate, formatBytes)} ·{" "}
							{fmtEta(stats.eta)}
						</span>
						<Button size="sm" variant="ghost" type="button" onClick={onCancel}>
							<X data-icon="inline-start" className="size-3.5" />
							Cancel
						</Button>
					</div>
				</motion.div>
			) : null}

			{state === "error" && file ? (
				<motion.div
					key="error"
					{...fade}
					className="rounded-md border border-destructive/60 bg-destructive/5 p-4"
				>
					<div className="flex items-center justify-between gap-3 text-sm">
						<span className="truncate font-mono">{file.name}</span>
						<span className="tabular-nums text-destructive">Upload failed</span>
					</div>
					{error ? (
						<p className="mt-2 text-xs text-destructive/90">{error}</p>
					) : null}
					<div className="mt-3 flex items-center gap-2">
						<Button size="sm" variant="outline" type="button" onClick={onRetry}>
							<Loader2 data-icon="inline-start" className="size-3.5" />
							Retry
						</Button>
						<Button size="sm" variant="ghost" type="button" onClick={onCancel}>
							Cancel
						</Button>
					</div>
				</motion.div>
			) : null}

			{state === "done" ? (
				<motion.div
					key="done"
					initial={reduce ? false : { opacity: 0, scale: 0.96 }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: 0.98 }}
					transition={{ duration: 0.22, ease: EASE }}
					className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-4"
				>
					<div className="flex items-center justify-between gap-3 text-sm">
						<span className="inline-flex min-w-0 items-center gap-2 truncate">
							<CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
							<span className="truncate font-mono">
								{file?.name ?? "Uploaded"}
							</span>
						</span>
						<Button size="sm" variant="ghost" type="button" onClick={onReset}>
							Upload another
						</Button>
					</div>
					{doneSummary ? (
						<div className="mt-2 text-xs text-muted-foreground">
							{doneSummary}
						</div>
					) : null}
				</motion.div>
			) : null}
		</AnimatePresence>
	);
}
